import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Pill,
  Activity,
  Apple,
  Dumbbell,
  LogOut,
  CalendarDays,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard", roles: ["admin", "receptionist"] },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar", roles: ["admin"] },
  { to: "/patients", label: "Patients", icon: Users, testId: "nav-patients", roles: ["admin", "receptionist"] },
  { to: "/diseases", label: "Diseases", icon: Activity, testId: "nav-diseases", roles: ["admin"] },
  { to: "/medications", label: "Medications", icon: Pill, testId: "nav-medications", roles: ["admin"] },
  { to: "/diet", label: "Diet", icon: Apple, testId: "nav-diet", roles: ["admin"] },
  { to: "/exercise", label: "Exercise", icon: Dumbbell, testId: "nav-exercise", roles: ["admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const items = navItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role || "admin")
  );

  const sidebarInner = (
    <>
      <div className="px-5 py-5 border-b border-border flex items-center justify-between">
        <button
          data-testid="sidebar-brand"
          onClick={() => navigate("/")}
          className="flex items-center gap-3 group"
        >
          <div className="size-10 rounded-md bg-primary/5 grid place-items-center overflow-hidden">
            <img src="/logo.png" alt="Namya Health" className="size-9 object-contain" />
          </div>
          <div className="text-left">
            <div className="font-heading font-bold text-base leading-tight">Namya</div>
            <div className="text-[10px] text-muted-foreground tracking-widest uppercase">
              Health Admin
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden size-9 rounded-md grid place-items-center hover:bg-muted"
          data-testid="close-mobile-nav"
          aria-label="Close menu"
        >
          <X className="size-5" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-foreground/70 hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <item.icon className="size-[18px]" strokeWidth={1.6} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 text-xs text-muted-foreground">
          Signed in as
          <div className="text-foreground text-sm font-medium truncate">
            {user?.email}
          </div>
          {user?.role && (
            <div className="text-[10px] uppercase tracking-widest text-secondary font-bold mt-0.5">
              {user.role}
            </div>
          )}
        </div>
        <Button
          data-testid="logout-btn"
          variant="ghost"
          className="w-full justify-start text-foreground/80 hover:text-destructive"
          onClick={logout}
        >
          <LogOut className="size-4 mr-2" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background grain">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-border flex-col">
        {sidebarInner}
      </aside>

      {/* Sidebar — mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
        <aside
          className={`absolute left-0 top-0 h-full w-72 max-w-[86vw] bg-white border-r border-border flex flex-col transition-transform ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="mobile-nav-drawer"
        >
          {sidebarInner}
        </aside>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 relative z-10">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white/95 backdrop-blur border-b border-border">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="size-9 rounded-md grid place-items-center hover:bg-muted"
            data-testid="open-mobile-nav"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Namya Health" className="size-7 object-contain" />
            <span className="font-heading font-bold text-sm">Namya</span>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
